import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userService, type UserMutationResult, type CreateUserMutationResult } from "@/features/users/services/userService";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type {
  AssignRoleRequest,
  CreateUserRequest,
  InviteUserRequest,
  UpdateUserRequest,
  User,
} from "@/types/user";

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation<CreateUserMutationResult, Error, CreateUserRequest>({
    mutationFn: (data) => userService.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toastMutationSuccess(res, {
        fallbackMessage: "تم إنشاء المستخدم بنجاح",
      });
    },
    onError: (error) => handleApiError(error),
  });
}

type UpdateUserMutationInput = {
  id: string;
  data: UpdateUserRequest;
  silentSuccess?: boolean;
};

type AssignRoleMutationInput = {
  id: string;
  data: AssignRoleRequest;
  silentSuccess?: boolean;
};

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation<User, Error, UpdateUserMutationInput>({
    mutationFn: ({ id, data }) => userService.update(id, data),
    onSuccess: (res, { silentSuccess }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      if (!silentSuccess) {
        toastMutationSuccess(res, { fallbackMessage: "تم تحديث المستخدم بنجاح" });
      }
    },
    onError: (error) => handleApiError(error),
  });
}

export function useAssignRole() {
  const queryClient = useQueryClient();
  return useMutation<User, Error, AssignRoleMutationInput>({
    mutationFn: ({ id, data }) => userService.assignRole(id, data),
    onSuccess: (res, { silentSuccess }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      if (!silentSuccess) {
        toastMutationSuccess(res, { fallbackMessage: "تم تحديث الدور بنجاح" });
      }
    },
    onError: (error) => handleApiError(error),
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation<User, Error, string>({
    mutationFn: (id) => userService.deactivate(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toastMutationSuccess(res, { fallbackMessage: "تم تعطيل المستخدم بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => userService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toastMutationSuccess(undefined, { fallbackMessage: "تم حذف المستخدم بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation<UserMutationResult<User>, Error, InviteUserRequest>({
    mutationFn: (data) => userService.invite(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toastMutationSuccess(res, {
        fallbackMessage: "تم إرسال الدعوة بنجاح",
      });
    },
    onError: (error) => handleApiError(error),
  });
}
